import type { ResourceResponse, uuid } from '@/types/general'
import type { CompanyProduct, Product } from '@/types/packaging'

import axios from 'axios'
import { defineStore } from 'pinia'
import { SetOptional } from 'type-fest'
import { keyBy } from 'lodash'

import { useAuthStore } from '@/stores/auth-store'
import { CompanyProductData } from '@/types/packaging'
import { TransactionProduct } from '@/types/transaction'
import { useCompanyStore } from '@/stores/company-store'

type ProductOrderItem = { order: number; id: string }
const productColors = [
    '#069468',
    '#037EA3',
    '#F8BB54',
    '#E76767',
    '#6A8780',
    '#C0DFE8',
    '#829486',
    '#2f7FA8',
    '#E9BF94',
    '#DC2626',
    '#2A534A',
    '#037193',
    '#935F07',
    '#2C323A',
    '#056F4E',
    '#C0DFE8',
    '#51B495',
    '#818892',
    '#4FA5BF',
    '#B3B5B7',
    '#4B5563',
    '#F59E0B',
    '#841717',
    '#939598',
    '#9BD4C3',
    '#B7BBC1',
    '#9ACBDA',
    '#F1A8A8',
    '#FBD89D',
    '#D4D5D6',
]

interface ProductStoreState {
    fetching: boolean
    loaded: boolean
    selectedProducts: CompanyProduct[]
    defaultProducts: CompanyProduct[]
    products: Product[]
    modalOpen: boolean
    colors: Record<uuid, string>
}

type InputProduct = SetOptional<
    TransactionProduct,
    'deliveredFilled' | 'receivedFilled' | 'delivered' | 'received' | 'productId'
>

export const useProductStore = defineStore('ProductStore', {
    state: (): ProductStoreState => ({
        fetching: false,
        loaded: false,
        selectedProducts: [],
        defaultProducts: [],
        products: [],
        modalOpen: false,
        colors: {},
    }),

    getters: {
        nonCompanyProducts: (state) => {
            return state.products.filter(
                (product) =>
                    ![...state.defaultProducts, ...state.selectedProducts]
                        .map((p) => p.id)
                        .includes(product.id),
            )
        },
        companyProducts: (state) => {
            return [...state.defaultProducts, ...state.selectedProducts]
        },
    },

    actions: {
        findProduct(id: string) {
            return this.products.find((product) => product.id === id)
        },

        findCompanyProduct(id: string) {
            return this.companyProducts.find((product) => product.id === id)
        },

        companyProductRequest(companyId?: uuid) {
            const authStore = useAuthStore()

            return axios.get<ResourceResponse<CompanyProduct[]>>(
                window.route('packaging.company.products.index', {
                    company: companyId ?? authStore.companyId,
                }),
            )
        },

        setCompanyProducts(products: CompanyProduct[]) {
            this.selectedProducts = products.filter((p) => !p.default)
            this.defaultProducts = products.filter((p) => p.default)
        },

        async fetchCompanyProducts() {
            this.fetching = true

            const response = await this.companyProductRequest()
            this.setCompanyProducts(response.data.data)
            this.setProductColors()

            this.fetching = false
        },

        async fetchProducts() {
            if (!this.loaded) {
                this.fetching = true

                const licensedProductsRequest = axios.get<ResourceResponse<Product[]>>(
                    window.route('packaging.products.index'),
                )

                const [companyProductsResponse, licensedProductsResponse] = await Promise.all([
                    this.companyProductRequest(),
                    licensedProductsRequest,
                ])

                this.setCompanyProducts(companyProductsResponse.data.data)
                this.setProductColors()
                this.products = licensedProductsResponse.data.data
                this.fetching = false
                this.loaded = true
            }
        },

        async addProductToCompany(product: Product) {
            const authStore = useAuthStore()
            this.fetching = true

            await axios.put(
                window.route('packaging.company.products.update', {
                    company: authStore.companyId,
                    product: product.id,
                }),
            )

            this.selectedProducts.push({
                ...product,
                order: 0,
                favored: false,
                price: 0,
                externalProductId: null,
                bottomPalletFraction: null,
            })
            this.fetching = false
        },

        async manageProduct(productId: string, data: Partial<CompanyProductData> = {}) {
            const authStore = useAuthStore()
            this.fetching = true

            const response = await axios.put<ResourceResponse<CompanyProduct[]>>(
                window.route('packaging.company.products.update', {
                    company: authStore.companyId,
                    product: productId,
                }),
                data,
            )

            this.setCompanyProducts(response.data.data)
            this.fetching = false
        },

        async removeProductFromCompany(productId: string) {
            const authStore = useAuthStore()
            this.fetching = true

            const response = await axios.delete<ResourceResponse<CompanyProduct[]>>(
                window.route('packaging.company.products.destroy', {
                    company: authStore.companyId,
                    product: productId,
                }),
            )

            this.setCompanyProducts(response.data.data)
            this.fetching = false
        },

        async saveDefaultOrder(products: ProductOrderItem[]) {
            const authStore = useAuthStore()
            this.fetching = true

            const response = await axios.post<
                { products: { order: number; id: string }[] },
                { data: ResourceResponse<CompanyProduct[]> }
            >(
                window.route('packaging.company.products.update-order', {
                    company: authStore.companyId,
                }),
                {
                    products,
                },
            )

            this.setCompanyProducts(response.data.data)
            this.fetching = false
        },

        /**
         * @param existingProducts
         * @param showAllProducts
         * @param customProducts When provided it'll not use the current company's products. The user role will also not have any effect
         */
        transactionProducts(
            existingProducts: InputProduct[],
            showAllProducts = false,
            customProducts?: CompanyProduct[],
        ): TransactionProduct[] {
            const companyStore = useCompanyStore()
            // When a custom list of default is provided we'll use that
            // When it's not we'll use the user role's products and fall back to the company products
            let defaultProducts = customProducts ?? this.companyProducts
            if (companyStore.userRole?.products && !customProducts && !showAllProducts) {
                const userRoleProducts = keyBy(companyStore.userRole.products, 'productId')
                defaultProducts = this.companyProducts.flatMap((p) => {
                    if (userRoleProducts[p.id]) {
                        return { ...p, order: userRoleProducts[p.id].order, default: true }
                    }
                    return []
                })
            }

            if (!showAllProducts) {
                defaultProducts = defaultProducts.filter((product) => product.default)
            }
            defaultProducts.sort((a, b) => a.order - b.order)

            const products: InputProduct[] = [
                ...existingProducts.filter(
                    (product) => (product?.delivered ?? 0) !== 0 || (product?.received ?? 0) !== 0,
                ),
            ]
            const existingProductsIds = products.map((p) => p.id)
            for (const product of defaultProducts) {
                if (!existingProductsIds.includes(product.id)) {
                    products.push(product)
                }
            }

            return products.map(
                (p): TransactionProduct => ({
                    ...p,
                    delivered: p.delivered ?? 0,
                    received: p.received ?? 0,
                    deliveredFilled: p.deliveredFilled ?? false,
                    receivedFilled: p.receivedFilled ?? false,
                }),
            )
        },

        setProductColors() {
            const colors: Record<uuid, string> = {}
            for (const [index, product] of Object.entries(this.companyProducts)) {
                colors[product.id] = productColors[index as unknown as number]
            }
            this.colors = colors
        },

        getProductColor(index: number): string {
            return productColors[index]
        },
    },
})
